import { useUserStore } from "../../store/userStore";
import { useActiveAppStore } from "../../store/activeAppStore";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useVehiclesSubscription } from "../../hooks/useVehiclesSubscription";
import { isMenuItemEnabled } from "../../config/appsConfig";
import { brand } from "../../config/theme";
import { incrementAdminFetchGeneration } from "../../utils/adminFetchGeneration";
import { setAdminAbortSignal } from "../../utils/api";
import { useDashboardCounts } from "../../features/dashboard/useDashboardCounts";
import Swal from "sweetalert2";
import { useStatusesStore } from "../../store/admin/statusesStore";
import { faGrip } from "@fortawesome/free-solid-svg-icons";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import CompanySelector from "../../components/base/CompanySelector";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import { MONITOR_ROLE_ID } from "../../hooks/useIsMonitor";

export default function AdminLayout() {
  const user = useUserStore((state) => state.user);
  const filteredCategories = useFilteredCategories();
  const userRole = localStorage.getItem("userRole");
  const isWebServicesRole = userRole === "web_services";
  const hasRedirected = useRef(false);

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const handleToggleSubmenu = (menuId: string) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId) ? prev.filter((id) => id !== menuId) : [...prev, menuId]
    );
  };

  useVehiclesSubscription();

  // Set active app for dynamic header title/subtitle and icon
  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);
  useEffect(() => {
    setActiveApp(
      "admin",
      t("apps.admin.name"),
      t("apps.admin.description"),
      faGrip,
      "bg-blue-500"
    );
  }, [setActiveApp, t]);

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const navigate = useNavigate();
  const location = useLocation();

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === "admin") {
      closeAppsSidebar();
      return;
    }

    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);

  // El rol web_services solo puede ver Notificaciones dentro de Administración
  // de Flotilla; la app entra por /admin/units, así que lo redirigimos.
  useEffect(() => {
    if (
      isWebServicesRole &&
      !location.pathname.startsWith("/admin/notifications")
    ) {
      navigate("/admin/notifications", { replace: true });
    }
  }, [isWebServicesRole, location.pathname, navigate]);

  const companyId = useCompaniesStore((state) => state.companyId);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);
  const companyUser = useCompaniesStore((state) => state.companyUser);
  const setCompanyUser = useCompaniesStore((state) => state.setCompanyUser);

  const isReseller = (localStorage.getItem("userRoleId") || "") === ADMIN_RESELLER_ROLE_ID;

  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore(
    (state) => state.fetchAllCompanies,
  );

  const fetchStatuses = useStatusesStore((state) => state.fetchStatuses);

  const { data: counts, isLoading: countsLoading } =
    useDashboardCounts(companyId);

  const unitsCount = counts?.units ?? 0;
  const trailersCount = counts?.trailers ?? 0;
  const devicesCount = counts?.devices ?? 0;
  const driversCount = counts?.drivers ?? 0;
  const groupsCount = counts?.groups ?? 0;
  const geofencesCount = counts?.geofences ?? 0;
  const pointOfInterestsCount = counts?.pointOfInterests ?? 0;
  const routesCount = counts?.routes ?? 0;
  const tripsCount = counts?.trips ?? 0;
  const trackingLinksCount = counts?.trackingLinks ?? 0;
  const notificationsCount = counts?.notifications ?? 0;
  const trackingReplaysCount = counts?.trackingReplays ?? 0;
  const usersCount = counts?.users ?? 0;
  const sessionsCount = counts?.sessions ?? 0;
  const avisosCount = counts?.avisos ?? 0;

  const previousCompanyId = useRef<string | null | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Sincronizar companyId y companyUser desde localStorage al montar
  useEffect(() => {
    const storedCompanyId = localStorage.getItem("companyId");
    const storedCompanyUser = localStorage.getItem("userCompanyId");

    if (storedCompanyId && storedCompanyId !== companyId) {
      setCompanyId(storedCompanyId);
    }

    if (storedCompanyUser && storedCompanyUser !== companyUser) {
      setCompanyUser(storedCompanyUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar todas las compañías para el selector cuando es reseller
  useEffect(() => {
    if (isReseller) {
      fetchAllCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReseller]);

  // Cleanup abort signal on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setAdminAbortSignal(null);
    };
  }, []);

  useEffect(() => {
    fetchStatuses({ limit: 200, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      previousCompanyId.current === companyId &&
      previousCompanyId.current !== undefined
    ) {
      return;
    }
    previousCompanyId.current = companyId;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setAdminAbortSignal(abortControllerRef.current.signal);
    incrementAdminFetchGeneration();
  }, [companyId]);

  const menuItems = isWebServicesRole
    ? [
      {
        id: "notifications",
        label: t("sidebarMenu.notifications"),
        icon: "ri-notification-line",
        count: notificationsCount,
        loading: countsLoading,
      },
    ]
    : [
      {
        id: "units",
        label: t("sidebarMenu.units"),
        icon: "ri-truck-line",
        count: unitsCount,
        loading: countsLoading,
      },
      {
        id: "trailers",
        label: t("sidebarMenu.trailers"),
        icon: "ri-box-3-line",
        count: trailersCount,
        loading: countsLoading,
      },
      {
        id: "devices",
        label: t("sidebarMenu.devices"),
        icon: "ri-smartphone-line",
        count: devicesCount,
        loading: countsLoading,
      },
      {
        id: "drivers",
        label: t("sidebarMenu.drivers"),
        icon: "ri-steering-2-line",
        count: driversCount,
        loading: countsLoading,
      },
      {
        id: "fleet-costs",
        label: t("sidebarMenu.fleetCosts"),
        icon: "ri-money-dollar-circle-line",
      },
      {
        id: "groups",
        label: t("sidebarMenu.groups"),
        icon: "ri-group-line",
        count: groupsCount,
        loading: countsLoading,
      },
      {
        id: "geofences",
        label: t("sidebarMenu.geofences"),
        icon: "ri-map-pin-range-line",
        count: geofencesCount,
        loading: countsLoading,
      },
      {
        id: "pointOfInterests",
        label: t("sidebarMenu.pointOfInterests"),
        icon: "ri-map-pin-line",
        count: pointOfInterestsCount,
        loading: countsLoading,
      },
      {
        id: "routes",
        label: t("sidebarMenu.routes"),
        icon: "ri-route-line",
        count: routesCount,
        loading: countsLoading,
      },
      {
        id: "trips",
        label: t("sidebarMenu.trips"),
        icon: "ri-road-map-line",
        count: tripsCount,
        loading: countsLoading,
      },
      {
        id: "tracking-links",
        label: t("sidebarMenu.trackingLinks"),
        icon: "ri-link",
        count: trackingLinksCount,
        loading: countsLoading,
        disabled: false,
        tooltip: t("sidebarMenu.comingSoon"),
      },
      {
        id: "notifications",
        label: t("sidebarMenu.notifications"),
        icon: "ri-notification-line",
        count: notificationsCount,
        loading: countsLoading,
      },
      {
        id: "tracking-replays",
        label: t("sidebarMenu.trackingReplays"),
        icon: "ri-route-line",
        count: trackingReplaysCount,
        loading: countsLoading,
      },
      {
        id: "usuarios",
        label: t("sidebarMenu.users"),
        icon: "ri-user-settings-line",
        count: usersCount,
        loading: countsLoading,
      },
      {
        id: "sessions",
        label: t("sidebarMenu.sessions"),
        icon: "ri-time-line",
        count: sessionsCount,
        loading: countsLoading,
      },
      {
        id: "avisos",
        label: t("sidebarMenu.avisos"),
        icon: "ri-megaphone-line",
        count: avisosCount,
        loading: countsLoading,
      },
    ];

  // Filtrar menú para monitoristas: solo permitir units, devices, trailers, geofences, drivers, recordings
  const userRoleId = localStorage.getItem("userRoleId");
  const isMonitorista = userRoleId === MONITOR_ROLE_ID;
  let filteredMenuItems = isMonitorista
    ? menuItems.filter((item) =>
        ["units", "devices", "trailers", "geofences", "drivers", "recordings"].includes(item.id)
      )
    : menuItems;

  // Filter menu items based on app config (central-gps-config.json, etc.)
  filteredMenuItems = filteredMenuItems.filter((item) =>
    isMenuItemEnabled("rastreo-telemetria", "admin", item.id),
  );

  // Calcular la suma total de elementos de los items visibles
  const totalElementos = filteredMenuItems.reduce(
    (acc, item) =>
      typeof item.count === "number" && !item.loading ? acc + item.count : acc,
    0,
  );

  // Calcular activos/inactivos SOLO para drivers y devices
  // const devices = useDevicesStore((state) => state.devices || []);
  // const drivers = useDriversStore((state) => state.drivers || []);

  // function getActiveInactiveDevices(arr: any[]): {
  //   activos: number;
  //   inactivos: number;
  // } {
  //   let activos = 0;
  //   let inactivos = 0;
  //   arr.forEach((item) => {
  //     if (item.status === "CONNECTED") {
  //       activos++;
  //     } else {
  //       inactivos++;
  //     }
  //   });
  //   return { activos, inactivos };
  // }

  // function getActiveInactiveDrivers(arr: any[]): {
  //   activos: number;
  //   inactivos: number;
  // } {
  //   let activos = 0;
  //   let inactivos = 0;
  //   arr.forEach((item) => {
  //     if (item.status === "active" || item.status === "ACTIVE") {
  //       activos++;
  //     } else {
  //       inactivos++;
  //     }
  //   });
  //   return { activos, inactivos };
  // }

  // const activosTotal =
  //   getActiveInactiveDevices(devices).activos +
  //   getActiveInactiveDrivers(drivers).activos;
  // const inactivosTotal =
  //   getActiveInactiveDevices(devices).inactivos +
  //   getActiveInactiveDrivers(drivers).inactivos;

  return (
    <div
      className={`h-screen flex flex-col ${isDarkMode ? "bg-[#1A1A1A]" : "bg-gray-50"}`}
    >
      {/* Banner de sesión por expirar */}
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}

      <Header
        onOpenAppsSidebar={openAppsSidebar}
        onToggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        isDarkMode={isDarkMode}
        isReseller={isReseller}
        companies={allCompanies}
        selectedCompany={companyId}
        onCompanyChange={setCompanyId}
      />
      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        <AppSidebarMenu
          menuItems={filteredMenuItems.map((item) => ({
            ...item,
            path: `/admin/${item.id}`,
          }))}
          isDarkMode={isDarkMode}
          dense
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
          onNavigate={(path) => {
            const itemId = path.replace("/admin/", "");
            if (itemId === "integrations") {
              Swal.fire({
                title: t("appsSidebar.integrationsTitle"),
                text: t("appsSidebar.comingSoon"),
                icon: "info",
                confirmButtonText: t("appsSidebar.accept"),
                confirmButtonColor: brand.primary,
              });
              return;
            }
            navigate(path);
          }}
          isActiveRoute={(itemId) => location.pathname.endsWith(itemId)}
          expandedMenus={expandedMenus}
          onToggleSubmenu={handleToggleSubmenu}
          companySelector={
            isReseller ? (
              <CompanySelector
                value={companyId ?? null}
                onChange={(id) => setCompanyId(id)}
                isDarkMode={isDarkMode}
                isClearable
                companies={allCompanies.length > 0 ? allCompanies : undefined}
                className="w-full"
              />
            ) : undefined
          }
          footerStats={{
            label: "Resumen",
            items: [
              { label: "Total elementos", value: totalElementos },
            ],
          }}
        />
        {/* Main Content */}
        <div className="flex-1 max-h-full overflow-hidden min-w-0 w-full">
          <Outlet />
        </div>
      </div>
      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="admin"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          username: user.username,
        } : undefined}
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
